import React, { useEffect, useMemo, useRef, useState } from 'react';

import { useOutsideClick } from '../js/Helpers';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp, faSlidersH, faTimes } from '@fortawesome/free-solid-svg-icons';
import { faSquare, faCheckSquare } from '@fortawesome/free-regular-svg-icons';

export const applySchoolDataFilters = (sourceData = [], selectedFilters = {}, categories = []) => {
	const active_categories = categories.filter(category => (selectedFilters[category.key] || []).length > 0);
	if(active_categories.length === 0) return Array.isArray(sourceData) ? sourceData : [];

	return (Array.isArray(sourceData) ? sourceData : []).filter(data_row => (
		active_categories.every(category => {
			const selected_values = selectedFilters[category.key] || [];
			if(typeof category.matchRow === 'function') return category.matchRow(data_row, selected_values);

			const row_value = data_row ? data_row[category.dataKey] : null;
			const row_values = Array.isArray(row_value) ? row_value : [row_value];
			const normalized_row_values = row_values.map(value => {
				if(value === null || value === undefined) return '';
				return `${value}`.trim();
			}).filter(value => value && value !== 'null' && value !== 'undefined');

			return selected_values.some(selected_value => {
				const selected_value_string = `${selected_value}`;
				if(category.noValueFilterValue !== undefined && `${category.noValueFilterValue}` === selected_value_string)
				{
					const no_value_string = `${category.noValueFilterValue}`;
					return normalized_row_values.length === 0 || normalized_row_values.every(row_filter_value => `${row_filter_value}` === no_value_string);
				}
				return normalized_row_values.some(row_filter_value => `${row_filter_value}` === selected_value_string);
			});
		})
	));
};

export default function SchoolDataFilter({ categories = [], onChange = () => {}, resultCount = null, resultLabel = 'rows' }) {
	const ref = useRef();
	const onChangeRef = useRef(onChange);
	const [visible, setVisible] = useState(false);
	const [openCategories, setOpenCategories] = useState([]);
	const [selectedFilters, setSelectedFilters] = useState({});

	onChangeRef.current = onChange;

	const normalizedCategories = useMemo(() => categories.filter(category => category && category.key && Array.isArray(category.options) && category.options.length > 0), [categories]);
	const selectedCount = useMemo(() => Object.keys(selectedFilters).reduce((total, category_key) => total + (selectedFilters[category_key] || []).length, 0), [selectedFilters]);
	const selectedFilterTags = useMemo(() => normalizedCategories.reduce((results, category) => {
		const selected_values = selectedFilters[category.key] || [];
		if(selected_values.length === 0) return results;

		selected_values.forEach(selected_value => {
			const selected_option = category.options.find(option => `${option.value}` === `${selected_value}`);
			if(!selected_option) return;

			results.push({
				category_key: category.key,
				category_title: category.title,
				value: `${selected_option.value}`,
				display: selected_option.display,
			});
		});

		return results;
	}, []).sort((filter_tag_a, filter_tag_b) => {
		const category_sort = `${filter_tag_a.category_title}`.localeCompare(`${filter_tag_b.category_title}`, undefined, {numeric:true});
		if(category_sort !== 0) return category_sort;
		return `${filter_tag_a.display}`.localeCompare(`${filter_tag_b.display}`, undefined, {numeric:true});
	}), [normalizedCategories, selectedFilters]);

	useOutsideClick(ref, () => setVisible(false));

	useEffect(() => {
		setSelectedFilters(current_filters => {
			const valid_category_keys = new Set(normalizedCategories.map(category => category.key));
			const next_filters = {};
			Object.keys(current_filters).forEach(category_key => {
				if(valid_category_keys.has(category_key)) next_filters[category_key] = current_filters[category_key];
			});
			return next_filters;
		});
	}, [normalizedCategories]);

	useEffect(() => {
		if(normalizedCategories.length === 1)
		{
			setOpenCategories([normalizedCategories[0].key]);
			return;
		}

		setOpenCategories(current_categories => {
			const valid_category_keys = new Set(normalizedCategories.map(category => category.key));
			return current_categories.filter(category_key => valid_category_keys.has(category_key));
		});
	}, [normalizedCategories]);

	useEffect(() => {
		onChangeRef.current(selectedFilters);
	}, [selectedFilters]);

	const toggleCategory = (category_key) => {
		setOpenCategories(current_categories => (
			current_categories.includes(category_key)
				? current_categories.filter(open_category => open_category !== category_key)
				: [...current_categories, category_key]
		));
	};

	const toggleFilter = (category_key, filter_value) => {
		setSelectedFilters(current_filters => {
			const current_category_filters = current_filters[category_key] || [];
			const normalized_filter_value = `${filter_value}`;
			const next_category_filters = current_category_filters.includes(normalized_filter_value)
				? current_category_filters.filter(current_filter => current_filter !== normalized_filter_value)
				: [...current_category_filters, normalized_filter_value];
			const next_filters = {...current_filters};

			if(next_category_filters.length === 0) delete next_filters[category_key];
			else next_filters[category_key] = next_category_filters;

			return next_filters;
		});
	};

	const removeFilter = (category_key, filter_value) => {
		setSelectedFilters(current_filters => {
			const current_category_filters = current_filters[category_key] || [];
			const next_category_filters = current_category_filters.filter(current_filter => `${current_filter}` !== `${filter_value}`);
			const next_filters = {...current_filters};

			if(next_category_filters.length === 0) delete next_filters[category_key];
			else next_filters[category_key] = next_category_filters;

			return next_filters;
		});
	};

	if(normalizedCategories.length === 0) return null;

	return (
		<>
			<div className='school-data-filter-container' ref={ref}>
				<div className='school-data-filter-button' onClick={() => setVisible(!visible)}>
					<FontAwesomeIcon icon={faSlidersH}/>
					{selectedCount > 0 &&
						<div className='list-filter-selected-count'>{selectedCount}</div>
					}
				</div>
				{visible &&
					<div className='list-filter-options-container click-restricted school-data-filter-options-container'>
						<div className='small-text'>Filter by:</div>
						{normalizedCategories.map(category => {
							const category_filters = selectedFilters[category.key] || [];
							const category_open = openCategories.includes(category.key);

							return (
								<div key={category.key}>
									<div className='list-filter-title-container' onClick={() => toggleCategory(category.key)}>
										<div className='capitalize list-filter-title-label'>
											{category.title}
											{category_filters.length > 0 &&
												<span className='list-filter-title-selected-indicator'>{category_filters.length}</span>
											}
										</div>
										<FontAwesomeIcon className='small-text' icon={category_open ? faChevronUp : faChevronDown}/>
									</div>
									{category_open &&
										category.options.map(option => {
											const option_value = `${option.value}`;
											const option_selected = category_filters.includes(option_value);

											return (
												<div className='parent-div list-filter-option click-restricted' key={`${category.key}-${option_value}`} onClick={() => toggleFilter(category.key, option_value)}>
													<div className='list-filter-option-name click-restricted'>{option.display}</div>
													<FontAwesomeIcon className={`${option_selected ? 'fas-checkbox-checked' : 'fas-checkbox-unchecked'} click-restricted`} icon={option_selected ? faCheckSquare : faSquare}/>
												</div>
											);
										})
									}
								</div>
							);
						})}
					</div>
				}
			</div>
			{selectedFilterTags.length > 0 &&
				<div className='mb-selected-filters-bar school-data-selected-filters-bar'>
					<div className='mb-selected-filters-text'>Filters:</div>
					{Number.isFinite(resultCount) &&
						<div className='school-data-filter-result-count'>Showing {resultCount.toLocaleString()} {resultLabel}</div>
					}
					{selectedFilterTags.map(filter_tag => (
						<div className='mb-selected-filters-filter-tag' key={`${filter_tag.category_key}-${filter_tag.value}`}>
							<div className='mb-selected-filters-filter-tag-inner-container'>
								<span>{filter_tag.display}</span>
								<FontAwesomeIcon className='dark-gray-link' icon={faTimes} onClick={() => removeFilter(filter_tag.category_key, filter_tag.value)}/>
							</div>
						</div>
					))}
				</div>
			}
		</>
	);
}
